import { RootStateInterface } from '@/store/rootState';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { FactFindApi } from '../api/factFind';
import {
  FactFindTemplateInterface,
  PresetFactFindCategoryInterface,
  PresetFactFindQuestionInterface,
} from '../interfaces/factFind';

export interface FactFindStateInterface {
  templatesByCompanyId: Record<string, FactFindTemplateInterface[]>;
  presetCategories: PresetFactFindCategoryInterface[];
  presetQuestions: PresetFactFindQuestionInterface[];
  presetsLoaded: boolean;
}

interface FetchTemplatesPayload {
  companyId: number;
  forceRefresh?: boolean;
}

interface FetchPresetPayload {
  forceRefresh?: boolean;
}

const templatesInFlight: Record<
  string,
  Promise<FactFindTemplateInterface[]> | undefined
> = {};
let presetCategoriesInFlight: Promise<PresetFactFindCategoryInterface[]> | null = null;
let presetQuestionsInFlight: Promise<PresetFactFindQuestionInterface[]> | null = null;

export const state: FactFindStateInterface = {
  templatesByCompanyId: {},
  presetCategories: [],
  presetQuestions: [],
  presetsLoaded: false,
};

export const mutations: MutationTree<FactFindStateInterface> = {
  setTemplatesForCompany(
    oldState,
    payload: { companyId: number; templates: FactFindTemplateInterface[] },
  ) {
    oldState.templatesByCompanyId = {
      ...oldState.templatesByCompanyId,
      [payload.companyId.toString()]: payload.templates,
    };
  },
  clearTemplatesForCompany(oldState, companyId: number) {
    const next = { ...oldState.templatesByCompanyId };
    delete next[companyId.toString()];
    oldState.templatesByCompanyId = next;
  },
  setPresetCategories(oldState, categories: PresetFactFindCategoryInterface[]) {
    oldState.presetCategories = categories;
    oldState.presetsLoaded =
      oldState.presetCategories.length > 0 && oldState.presetQuestions.length > 0;
  },
  setPresetQuestions(oldState, questions: PresetFactFindQuestionInterface[]) {
    oldState.presetQuestions = questions;
    oldState.presetsLoaded =
      oldState.presetCategories.length > 0 && oldState.presetQuestions.length > 0;
  },
  reset(oldState) {
    oldState.templatesByCompanyId = {};
    oldState.presetCategories = [];
    oldState.presetQuestions = [];
    oldState.presetsLoaded = false;
  },
};

export const getters: GetterTree<FactFindStateInterface, RootStateInterface> = {
  templatesForCompany: (st) => (companyId: number) => {
    return st.templatesByCompanyId[companyId.toString()] || [];
  },
};

export const actions: ActionTree<FactFindStateInterface, RootStateInterface> = {
  async fetchTemplates({ state: st, commit }, payload: FetchTemplatesPayload) {
    const companyId = payload.companyId;
    if (!companyId) {
      return [];
    }
    const key = companyId.toString();
    if (!payload.forceRefresh && st.templatesByCompanyId[key]) {
      return st.templatesByCompanyId[key];
    }

    if (templatesInFlight[key]) {
      return templatesInFlight[key];
    }

    const run = async () => {
      const api = new FactFindApi();
      const resp = await api.getTemplatesByCompanyId(companyId);
      const templates = resp?.data ?? [];
      commit('setTemplatesForCompany', { companyId, templates });
      return templates;
    };

    templatesInFlight[key] = run().finally(() => {
      delete templatesInFlight[key];
    });

    return templatesInFlight[key];
  },

  async fetchPresetCategories({ state: st, commit }, payload: FetchPresetPayload = {}) {
    if (!payload.forceRefresh && st.presetCategories.length > 0) {
      return st.presetCategories;
    }
    if (presetCategoriesInFlight) {
      return presetCategoriesInFlight;
    }

    const run = async () => {
      const api = new FactFindApi();
      const resp = await api.getAllPresetCategories();
      const categories = (resp?.data ?? []).sort((a, b) => a.id - b.id);
      commit('setPresetCategories', categories);
      return categories;
    };

    presetCategoriesInFlight = run().finally(() => {
      presetCategoriesInFlight = null;
    });
    return presetCategoriesInFlight;
  },

  async fetchPresetQuestions({ state: st, commit }, payload: FetchPresetPayload = {}) {
    if (!payload.forceRefresh && st.presetQuestions.length > 0) {
      return st.presetQuestions;
    }
    if (presetQuestionsInFlight) {
      return presetQuestionsInFlight;
    }

    const run = async () => {
      const api = new FactFindApi();
      const resp = await api.getAllPresetQuestions();
      const questions = (resp?.data ?? []).sort((a, b) => a.id - b.id);
      commit('setPresetQuestions', questions);
      return questions;
    };

    presetQuestionsInFlight = run().finally(() => {
      presetQuestionsInFlight = null;
    });
    return presetQuestionsInFlight;
  },

  async preloadStaticData({ dispatch, rootState }, payload: FetchPresetPayload = {}) {
    const companyId = rootState.brokers?.company?.id;
    await Promise.all([
      dispatch('fetchPresetCategories', payload),
      dispatch('fetchPresetQuestions', payload),
      companyId
        ? dispatch('fetchTemplates', { companyId, forceRefresh: payload.forceRefresh })
        : Promise.resolve([]),
    ]);
  },

  invalidateTemplatesForCompany({ commit }, companyId: number) {
    commit('clearTemplatesForCompany', companyId);
  },
};

const namespaced = true;

export const factFind: Module<FactFindStateInterface, RootStateInterface> = {
  namespaced,
  state,
  mutations,
  getters,
  actions,
};
